import { useEffect, useMemo, useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
// import { Input } from '@/components/ui/input';
// import { Separator } from '@/components/ui/separator';
import {
  fetchCustomers,
  softDeleteCustomer,
  reactivateCustomer,
  impersonateCustomer,
  createEndUserForCustomer,
  fetchEndUsersForCustomer,
  createCustomer,
  updateCustomer,
  fetchAdminUsers,
  createAdminUser,
  deactivateAdminUser,
  fetchSearchSettings,
  updateSearchSettings,
} from "@/services/adminDashboard";
import { useNavigate } from "react-router-dom";
import { logoutAll } from "@/services/adminCustomerAuth";
import JgsSweepCard from "@/components/admin/JgsSweepCard";
import ModelCurrencySummaryCard from "@/components/admin/modelCurrency/ModelCurrencySummaryCard";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

type Customer = {
  id?: string;
  _id?: string;
  name: string;
  isActive?: boolean;
  createdAt?: string;
  maxUsers?: number;
};
type AdminUser = { _id: string; email: string; isActive?: boolean; createdAt?: string };
// Types used previously for inline listings have been removed in favor of a dedicated customer page

const AdminDashboard = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const navigate = useNavigate();
  const [createFor, setCreateFor] = useState<{ id: string; name: string } | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdPassword, setCreatedPassword] = useState<string | null>(null);
  const [createdEmail, setCreatedEmail] = useState<string | null>(null);

  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState("");
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [createCustomerError, setCreateCustomerError] = useState<string | null>(null);
  const [createdCustomerName, setCreatedCustomerName] = useState<string | null>(null);

  const [createAdminOpen, setCreateAdminOpen] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const [newAdminPasswordConfirm, setNewAdminPasswordConfirm] = useState("");
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [createAdminError, setCreateAdminError] = useState<string | null>(null);

  const [searchDiffTolerance, setSearchDiffTolerance] = useState<string>("");
  const [searchRankTolerance, setSearchRankTolerance] = useState<string>("");
  const [savingSettings, setSavingSettings] = useState(false);

  const [maxUsersFor, setMaxUsersFor] = useState<{
    id: string;
    name: string;
    current: number;
  } | null>(null);
  const [maxUsersValue, setMaxUsersValue] = useState<string>("");
  const [savingMaxUsers, setSavingMaxUsers] = useState(false);
  const [maxUsersError, setMaxUsersError] = useState<string | null>(null);

  const normalized = useMemo(
    () =>
      customers.map(c => ({
        id: c.id || c._id,
        name: c.name,
        isActive: c.isActive !== false,
        createdAt: c.createdAt,
        maxUsers: c.maxUsers,
      })),
    [customers]
  );

  const activeCustomers = useMemo(() => normalized.filter(c => c.isActive), [normalized]);
  const deletedCustomers = useMemo(() => normalized.filter(c => !c.isActive), [normalized]);

  useEffect(() => {
    (async () => {
      try {
        const [customersList, adminsList, settings] = await Promise.all([
          fetchCustomers(true), // Include deleted customers so they can be reactivated
          fetchAdminUsers(),
          fetchSearchSettings(),
        ]);
        setCustomers(customersList);
        setAdminUsers(adminsList);
        setSearchDiffTolerance(String(settings.scoringDifferentialTolerance));
        setSearchRankTolerance(String(settings.classRankTolerance));
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        toast({ title: "Failed to load data", description: message, variant: "destructive" });
      } finally {
        setLoading(false);
      }
    })();
  }, [toast]);

  async function viewCustomer(customerId: string) {
    try {
      const result = (await impersonateCustomer(customerId)) as { token?: string };
      // Store the impersonation token in sessionStorage for cross-origin cookie support
      if (result?.token) {
        sessionStorage.setItem("recruit_ai_token", result.token);
      }
      navigate("/customer/dashboard");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: "Failed to open customer", description: message, variant: "destructive" });
    }
  }

  async function handleSoftDelete(customerId: string, name: string) {
    const ok = window.confirm(
      `Soft delete customer "${name}"? This can be reversed by re-activating later.`
    );
    if (!ok) return;
    try {
      await softDeleteCustomer(customerId);
      setCustomers(prev =>
        prev.map(c => (c.id === customerId || c._id === customerId ? { ...c, isActive: false } : c))
      );
      toast({ title: "Customer soft-deleted", description: name });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: "Delete failed", description: message, variant: "destructive" });
    }
  }

  async function handleReactivate(customerId: string, name: string) {
    const ok = window.confirm(`Reactivate customer "${name}"?`);
    if (!ok) return;
    try {
      await reactivateCustomer(customerId);
      setCustomers(prev =>
        prev.map(c => (c.id === customerId || c._id === customerId ? { ...c, isActive: true } : c))
      );
      toast({ title: "Customer reactivated", description: name });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: "Reactivation failed", description: message, variant: "destructive" });
    }
  }

  function openCreateDialog(customerId: string, name: string) {
    setCreateFor({ id: customerId, name });
    setNewEmail("");
    setCreateError(null);
    setCreatedPassword(null);
    setCreatedEmail(null);
  }

  function openCreateCustomerDialog() {
    setCreateCustomerOpen(true);
    setNewCustomerName("");
    setCreateCustomerError(null);
    setCreatedCustomerName(null);
  }

  function openCreateAdminDialog() {
    setCreateAdminOpen(true);
    setNewAdminEmail("");
    setNewAdminPassword("");
    setNewAdminPasswordConfirm("");
    setCreateAdminError(null);
  }

  async function submitCreateCustomer() {
    const trimmed = String(newCustomerName || "").trim();
    if (!trimmed) {
      setCreateCustomerError("Customer name is required");
      return;
    }
    try {
      setCreatingCustomer(true);
      setCreateCustomerError(null);
      const result = await createCustomer(trimmed);
      const name = result?.customer?.name || trimmed;
      setCreatedCustomerName(name);

      // Refresh customer list
      try {
        const list = await fetchCustomers(false);
        setCustomers(list);
      } catch {
        // Ignore refresh errors
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setCreateCustomerError(message || "Failed to create customer");
    } finally {
      setCreatingCustomer(false);
    }
  }

  async function submitCreateEndUser() {
    if (!createFor) return;
    const trimmed = String(newEmail || "")
      .toLowerCase()
      .trim();
    if (!trimmed) {
      setCreateError("Email is required");
      return;
    }
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (!isValid) {
      setCreateError("Please enter a valid email address");
      return;
    }
    try {
      setCreating(true);
      setCreateError(null);
      // Pre-check: see if an end user with this email already exists for the customer
      try {
        const res = await fetchEndUsersForCustomer(createFor.id);
        const existing = res?.users ?? res;
        if (Array.isArray(existing)) {
          const found = existing.some(
            (u: { email?: string }) =>
              String(u?.email || "")
                .toLowerCase()
                .trim() === trimmed
          );
          if (found) {
            setCreateError("An end user with this email already exists for this customer");
            setCreating(false);
            return;
          }
        }
      } catch {
        // Ignore pre-check errors
      }
      const result = await createEndUserForCustomer(createFor.id, trimmed);
      const temp = result?.temporaryPassword as string | undefined;
      setCreatedEmail(result?.email || trimmed);
      setCreatedPassword(temp || null);
      // Auto-copy if we have a password
      if (temp && typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(temp);
        } catch {
          // Ignore clipboard errors
        }
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setCreateError(message || "Failed to create end user");
    } finally {
      setCreating(false);
    }
  }

  async function submitCreateAdmin() {
    const trimmedEmail = String(newAdminEmail || "")
      .toLowerCase()
      .trim();
    const password = String(newAdminPassword || "");
    const passwordConfirm = String(newAdminPasswordConfirm || "");

    if (!trimmedEmail) {
      setCreateAdminError("Email is required");
      return;
    }
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
    if (!isValid) {
      setCreateAdminError("Please enter a valid email address");
      return;
    }
    if (!password) {
      setCreateAdminError("Password is required");
      return;
    }
    if (password.length <= 10) {
      setCreateAdminError("Password must be more than 10 characters");
      return;
    }
    if (password !== passwordConfirm) {
      setCreateAdminError("Passwords do not match");
      return;
    }

    try {
      setCreatingAdmin(true);
      setCreateAdminError(null);
      await createAdminUser(trimmedEmail, password);

      toast({
        title: "Admin user created",
        description: `Successfully created admin: ${trimmedEmail}`,
      });

      // Refresh admin list
      try {
        const adminsList = await fetchAdminUsers();
        setAdminUsers(adminsList);
      } catch {
        // Ignore refresh errors
      }

      setCreateAdminOpen(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setCreateAdminError(message || "Failed to create admin user");
    } finally {
      setCreatingAdmin(false);
    }
  }

  async function handleDeactivateAdmin(adminId: string, email: string) {
    const ok = window.confirm(
      `Deactivate admin user "${email}"? They will no longer be able to log in.`
    );
    if (!ok) return;
    try {
      await deactivateAdminUser(adminId);
      setAdminUsers(prev => prev.map(a => (a._id === adminId ? { ...a, isActive: false } : a)));
      toast({ title: "Admin user deactivated", description: email });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: "Deactivation failed", description: message, variant: "destructive" });
    }
  }

  // Creating users now happens on the customer dashboard page
  async function handleLogout() {
    try {
      try {
        await logoutAll();
      } catch {
        // Ignore logout errors
      }
      navigate("/", { replace: true });
    } catch {
      // Fallback to home page
      navigate("/", { replace: true });
    }
  }

  async function handleSaveSearchSettings() {
    const diff = parseFloat(searchDiffTolerance);
    const rank = parseFloat(searchRankTolerance);
    if (isNaN(diff) || diff < 0) {
      toast({
        title: "Invalid value",
        description: "Scoring differential tolerance must be a non-negative number",
        variant: "destructive",
      });
      return;
    }
    if (isNaN(rank) || rank < 0) {
      toast({
        title: "Invalid value",
        description: "Class rank tolerance must be a non-negative number",
        variant: "destructive",
      });
      return;
    }
    try {
      setSavingSettings(true);
      await updateSearchSettings({ scoringDifferentialTolerance: diff, classRankTolerance: rank });
      toast({ title: "Search settings saved" });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: "Failed to save settings", description: message, variant: "destructive" });
    } finally {
      setSavingSettings(false);
    }
  }

  function openMaxUsersDialog(customerId: string, name: string, current: number) {
    setMaxUsersFor({ id: customerId, name, current });
    setMaxUsersValue(String(current ?? 10));
    setMaxUsersError(null);
  }

  async function submitMaxUsers() {
    if (!maxUsersFor) return;
    const parsed = parseInt(maxUsersValue, 10);
    if (isNaN(parsed) || parsed < 0) {
      setMaxUsersError("Must be a non-negative number");
      return;
    }
    try {
      setSavingMaxUsers(true);
      setMaxUsersError(null);
      await updateCustomer(maxUsersFor.id, { maxUsers: parsed });
      setCustomers(prev =>
        prev.map(c =>
          c.id === maxUsersFor.id || c._id === maxUsersFor.id ? { ...c, maxUsers: parsed } : c
        )
      );
      toast({ title: "Max users updated", description: `${maxUsersFor.name}: ${parsed} users` });
      setMaxUsersFor(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setMaxUsersError(message || "Failed to update");
    } finally {
      setSavingMaxUsers(false);
    }
  }

  if (loading) {
    return <div className="p-6">Loading customers…</div>;
  }

  return (
    <div className="p-4 max-w-5xl mx-auto space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Admin Users</CardTitle>
              <CardDescription>Manage admin accounts</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/recruit-admin/college-teams")}
              >
                College Teams
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/recruit-admin/program-news")}
              >
                Program News
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate("/recruit-admin/crm")}>
                CRM
              </Button>
              <Button variant="default" size="sm" onClick={openCreateAdminDialog}>
                Create Admin User
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {adminUsers.length === 0 ? (
            <div>No admin users found.</div>
          ) : (
            <div className="space-y-4">
              {adminUsers.map(admin => (
                <div key={admin._id} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-medium">{admin.email}</div>
                      <div className="text-sm text-muted-foreground">
                        {admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : ""}{" "}
                        {admin.isActive === false ? "(inactive)" : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {admin.isActive !== false && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeactivateAdmin(admin._id, admin.email)}
                        >
                          Deactivate
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Customers</CardTitle>
              <CardDescription>Manage customer organizations</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="default" size="sm" onClick={openCreateCustomerDialog}>
                Create Customer
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                Logout
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {activeCustomers.length === 0 ? (
            <div>No active customers found.</div>
          ) : (
            <div className="space-y-4">
              {activeCustomers.map(c => (
                <div key={c.id} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-sm text-muted-foreground">
                        Max users: {c.maxUsers ?? 10}
                        <Button
                          variant="link"
                          size="sm"
                          className="h-auto p-0 ml-1 text-xs"
                          onClick={() => openMaxUsersDialog(c.id, c.name, c.maxUsers ?? 10)}
                        >
                          edit
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="secondary" onClick={() => viewCustomer(c.id)}>
                        View Customer
                      </Button>
                      <Button variant="outline" onClick={() => openCreateDialog(c.id, c.name)}>
                        Create End User
                      </Button>
                      <Button variant="destructive" onClick={() => handleSoftDelete(c.id, c.name)}>
                        Soft Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Deleted Customers</CardTitle>
              <CardDescription>Soft-deleted customer organizations</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {deletedCustomers.length === 0 ? (
            <div>No deleted customers found.</div>
          ) : (
            <div className="space-y-4">
              {deletedCustomers.map(c => (
                <div key={c.id} className="border rounded-lg p-3 opacity-75">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-sm text-muted-foreground">(deleted)</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="default" onClick={() => handleReactivate(c.id, c.name)}>
                        Reactivate
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Search Settings</CardTitle>
              <CardDescription>
                Default tolerances used when the AI searches for matching teams
              </CardDescription>
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={handleSaveSearchSettings}
              disabled={savingSettings}
            >
              {savingSettings ? "Saving…" : "Save"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="text-sm font-medium">Scoring Differential Tolerance</div>
              <Input
                type="number"
                step="0.1"
                min="0"
                value={searchDiffTolerance}
                onChange={e => setSearchDiffTolerance(e.target.value)}
                disabled={savingSettings}
              />
              <div className="text-xs text-muted-foreground">
                How many strokes above/below a student's differential to include (e.g. 0.6)
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-sm font-medium">Class Rank Tolerance</div>
              <Input
                type="number"
                step="1"
                min="0"
                value={searchRankTolerance}
                onChange={e => setSearchRankTolerance(e.target.value)}
                disabled={savingSettings}
              />
              <div className="text-xs text-muted-foreground">
                How many positions above/below a student's class rank to include (e.g. 100)
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <JgsSweepCard />
      <ModelCurrencySummaryCard />
      <Dialog
        open={!!createFor}
        onOpenChange={open => {
          if (!open) {
            setCreateFor(null);
            setNewEmail("");
            setCreateError(null);
            setCreatedPassword(null);
            setCreatedEmail(null);
          }
        }}
      >
        <DialogContent
          onInteractOutside={e => {
            // Prevent accidental close while creating or after showing password
            if (creating || createdPassword) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Create End User</DialogTitle>
            <DialogDescription>
              {createFor ? `Create a login for "${createFor.name}".` : "Create a login."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <div className="text-sm font-medium">Email</div>
              <Input
                placeholder="email@example.com"
                type="email"
                value={newEmail}
                onChange={e => {
                  setNewEmail(e.target.value);
                  setCreateError(null);
                }}
                disabled={creating || !!createdPassword}
              />
              {createError ? <div className="text-sm text-destructive">{createError}</div> : null}
            </div>
            {createdPassword ? (
              <div className="space-y-2">
                <div className="text-sm text-muted-foreground">
                  Temporary password for {createdEmail}:
                </div>
                <div className="flex items-center gap-2">
                  <code className="rounded bg-muted px-2 py-1 font-mono">{createdPassword}</code>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={async () => {
                      try {
                        if (navigator?.clipboard?.writeText) {
                          await navigator.clipboard.writeText(createdPassword);
                        } else {
                          const ta = document.createElement("textarea");
                          ta.value = String(createdPassword);
                          ta.style.position = "fixed";
                          ta.style.left = "-9999px";
                          document.body.appendChild(ta);
                          ta.focus();
                          ta.select();
                          try {
                            document.execCommand("copy");
                          } catch {
                            // Ignore copy command errors
                          }
                          document.body.removeChild(ta);
                        }
                      } catch {
                        // Ignore clipboard errors
                      }
                    }}
                  >
                    Copy
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground">
                  This password is shown only once. Share it securely with the user.
                </div>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            {!createdPassword ? (
              <Button onClick={submitCreateEndUser} disabled={creating}>
                {creating ? "Creating…" : "Create"}
              </Button>
            ) : (
              <Button onClick={() => setCreateFor(null)}>Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={createCustomerOpen}
        onOpenChange={open => {
          if (!open) {
            setCreateCustomerOpen(false);
            setNewCustomerName("");
            setCreateCustomerError(null);
            setCreatedCustomerName(null);
          }
        }}
      >
        <DialogContent
          onInteractOutside={e => {
            if (creatingCustomer) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Create Customer</DialogTitle>
            <DialogDescription>Create a new customer organization.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <div className="text-sm font-medium">
                Customer Name <span className="text-destructive">*</span>
              </div>
              <Input
                placeholder="e.g. Golf Academy"
                type="text"
                value={newCustomerName}
                onChange={e => {
                  setNewCustomerName(e.target.value);
                  setCreateCustomerError(null);
                }}
                disabled={creatingCustomer || !!createdCustomerName}
              />
            </div>

            {createCustomerError ? (
              <div className="text-sm text-destructive">{createCustomerError}</div>
            ) : null}
            {createdCustomerName ? (
              <div className="text-sm font-semibold text-muted-foreground">
                Customer created: {createdCustomerName}
              </div>
            ) : null}
          </div>
          <DialogFooter>
            {!createdCustomerName ? (
              <Button onClick={submitCreateCustomer} disabled={creatingCustomer}>
                {creatingCustomer ? "Creating…" : "Create"}
              </Button>
            ) : (
              <Button onClick={() => setCreateCustomerOpen(false)}>Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={createAdminOpen}
        onOpenChange={open => {
          if (!open) {
            setCreateAdminOpen(false);
            setNewAdminEmail("");
            setNewAdminPassword("");
            setNewAdminPasswordConfirm("");
            setCreateAdminError(null);
          }
        }}
      >
        <DialogContent
          onInteractOutside={e => {
            // Prevent accidental close while creating
            if (creatingAdmin) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Create Admin User</DialogTitle>
            <DialogDescription>
              Create a new admin account with full system access.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <div className="text-sm font-medium">
                Email <span className="text-destructive">*</span>
              </div>
              <Input
                placeholder="admin@example.com"
                type="email"
                value={newAdminEmail}
                onChange={e => {
                  setNewAdminEmail(e.target.value);
                  setCreateAdminError(null);
                }}
                disabled={creatingAdmin}
              />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-medium">
                Password <span className="text-destructive">*</span>
              </div>
              <Input
                placeholder="Enter password (>10 characters)"
                type="password"
                value={newAdminPassword}
                onChange={e => {
                  setNewAdminPassword(e.target.value);
                  setCreateAdminError(null);
                }}
                disabled={creatingAdmin}
              />
              <div className="text-xs text-muted-foreground">
                Password must be more than 10 characters
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-sm font-medium">
                Confirm Password <span className="text-destructive">*</span>
              </div>
              <Input
                placeholder="Confirm password"
                type="password"
                value={newAdminPasswordConfirm}
                onChange={e => {
                  setNewAdminPasswordConfirm(e.target.value);
                  setCreateAdminError(null);
                }}
                disabled={creatingAdmin}
              />
            </div>
            {createAdminError ? (
              <div className="text-sm text-destructive">{createAdminError}</div>
            ) : null}
          </div>
          <DialogFooter>
            <Button onClick={submitCreateAdmin} disabled={creatingAdmin}>
              {creatingAdmin ? "Creating…" : "Create Admin"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!maxUsersFor}
        onOpenChange={open => {
          if (!open) {
            setMaxUsersFor(null);
            setMaxUsersValue("");
            setMaxUsersError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Max Users</DialogTitle>
            <DialogDescription>
              {maxUsersFor ? `Set the maximum number of users for "${maxUsersFor.name}".` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <div className="text-sm font-medium">Maximum Users</div>
              <Input
                type="number"
                min="0"
                step="1"
                value={maxUsersValue}
                onChange={e => {
                  setMaxUsersValue(e.target.value);
                  setMaxUsersError(null);
                }}
                disabled={savingMaxUsers}
              />
              <div className="text-xs text-muted-foreground">
                The maximum number of end users this customer can invite.
              </div>
            </div>
            {maxUsersError ? <div className="text-sm text-destructive">{maxUsersError}</div> : null}
          </div>
          <DialogFooter>
            <Button onClick={submitMaxUsers} disabled={savingMaxUsers}>
              {savingMaxUsers ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
